import { TColors } from '@app/core/common.model';
import { IDiscounts } from '@app/core/discounts/models/discounts.model';
import { EPaymentSubTypes } from '@app/core/payment/enums/payment-sub-types.enum';
import { EPaymentTypes } from '@app/core/payment/enums/payment-types.enum';
import { IProductInfo } from '@app/core/product/models/product-info.model';
import { TProductSubType } from '@app/core/product/models/product-sub-type.type';
import { IPartyGuestList } from './party-guest-list/party-guest-list.modal';
import { TCheckoutType } from '@app/core/venue-config/models/venue-config.model';

export interface ITickets {
  upcomingBookings: IUpcomingBooking[];
  memberships: IMembership[];
  giftcards: IGiftCard[];
}
export interface IUpcomingBooking {
  bookingUniqueId: string;
  checkoutType?: TCheckoutType;
  /** Note that the created Date is without timezone and recorded in reference to the time of the venue - '2024-04-24T08:16:14.173' */
  createdDate: string;
  grandTotal: number;
  itemsCount: number;
  nextBookingItemProductSubType: TProductSubType;
  nextBookingItemStartDate: string;
  receiptNumber: number;
  remainderTotal: number;
}
export interface IMembership {
  activationDate?: string;
  bookingItemPartId: number;
  bookingDate: string;
  bookingUniqueId: string;
  cancellationDate?: string;
  cost: number;
  expiry: string;
  isStacked?: boolean;
  membershipId: string | number;
  membershipName: string;
  membershipNumber: string;
  membershipVariant: string;
  parentProductId: number;
  paymentFrequency: number;
  productId: number;
  recurringPaymentId?: number;
  status: number;
  statusDate?: string;
}

export interface IMembershipDetails extends IMembership, IPaymentDetails {
  canGuestCancelMembership: boolean;
  initialPaymentDate: string;
  lastPaymentDate: string;
  memberName?: string;
  membershipDescription: string;
  minimumPaymentsEndDate: string;
  nextPaymentDate: string;
  paymentHistory?: IPaymentHistory[];
  onlineSalesOpen: boolean;
  recurringDetailReference: string;
  receiptNumber: number;
  availableUpgrades: IMembershipUpgradesInfo[];
}

export interface IMembershipDetailsSelected extends IMembershipDetails {
  isSelected?: boolean;
}

export interface IMembershipUpgradesInfo {
  bookingItemId: number;
  bookingItemPartId: number;
  capturePaymentToken: boolean;
  nextPaymentDate: string;
  numberOfPayments: number;
  paymentFrequencyId: string;
  productCost: number;
  recurringPaymentStatus: string;
  upgradeAmount: number;
  upgradeDiscount: number;
  upgradeParentProductId: number;
  upgradePaymentFrequencyId: string;
  upgradeProductCost: number;
  upgradeProductId: number;
}

export interface IUpcomingBookingDetails extends IUpcomingBooking {
  bookingItems: IBookingItem[];
  creditCardFee: number;
  customerEmail: string;
  customerMobile: string;
  customerName: string;
  discount: number;
  discounts: IDiscounts[];
  grandTotal: number;
  nextBookingSessionDurationMinutes: number;
  payments: IPaymentDetails[];
  postage: number;
  status: number;
  subTotal: number;
  tax: number;
  feeTax: number;
  subTotalTax?: number;
  time?: string;
  transactionFee: number;
  hasAmountOwing: boolean;
  amountPaidExcludingCardFees: number;
  hasOnlineAccount?: boolean;
  hasRecurringBilling?: boolean;
  taxExclusive?: number;
  amountOwing?: number;
  giftcardDeducted?: number;
  guestList?: IPartyGuestList;
  totalIgnoringDeposit?: number;
  bookingName?: string;
  deposit?: number;
  depositFee?: number;
  tableNumber?: string;
  tip?: number;
  customerUniqueId?: string;
}

/** Should match - CheckoutBookingDetailsBookingItemsDTO */
export interface IBookingItem {
  associatedBookingItemId?: number;
  bookingDate: string;
  bookingEndDate: string;
  bookingItemId: number;
  canAddItems: boolean;
  canRemoveItems: boolean;
  canReschedule: boolean;
  description: string;
  imageUrl: string;
  isWaiverRequired: boolean;
  name: string;
  packageBookingItemId?: number;
  parentProductId: number;
  productCost: number;
  productId: number;
  productSubTypeId: number;
  productTypeId: number;
  quantity: number;
  sessionStart?: string;
  sessionEnd?: string;
  sessionName?: string;
  total: number;
  hideSessionDuration: boolean;
  isPartyPackageInclusion?: boolean;
  isPackageInclusion?: boolean;
  productPackageOptionSetId?: number;
  isGuestSelection?: boolean;
  modifiers?: IBookingItemModifier[];
  comments?: string;
  /** Local use only for party package booking items */
  packageBookingItems?: IBookingItem[];
  product?: IProductInfo;
  relatedOrderItemKey?: string;
  /** Local use only for addons for relationship */
  addonToProductId?: string;
  bookingItemIds?: number[];
  validDays?: number;
  validDaysFromRedemption?: number;
  additionalGuests: number;
}

export interface IBookingItemModifier {
  amount?: number;
  bookingItemId?: number;
  modifierAmount?: number;
  modifierId: string;
  modifierIsPercentage?: boolean;
  name?: string;
  quantity?: number;
  taxPercent?: number;
  /** local use only */
  totalModifierAmount?: number;
}

export interface ISavedPayment {
  variant: string;
  last4Digits: string;
  recurringDetailReference: string;
  usage: number;
  type: string;
  expiryMonth: string;
  expiryYear: string;
  isDefault: boolean;
  isExpired?: boolean;
  paymentIcon: string;
  paymentSubType: EPaymentSubTypes;
  status: string;
}

export interface IChangeRecurringPayment {
  paymentMethod: ISavedPayment;
  defaultPaymentMethod?: ISavedPayment;
}

export interface IPaymentRequestPayload {
  addNew: boolean;
  bookingUniqueIds?: string[];
  recurringDetailReference?: string;
  referenceToRemove?: string;
}

export interface IDefaultPaymentRequestPayload {
  bookingUniqueIds: string[];
}

export interface IPaymentResponse {
  paymentJwt: string;
  referenceId: string;
}

export interface IPaymentDetails {
  creditCardLast4Digits?: string;
  paymentSubType?: EPaymentSubTypes;
  paymentType: EPaymentTypes;
  total: number;
  giftcardNumber?: string;
}

export interface IPaymentDetailsDisplay extends IPaymentDetails {
  paymentMethod: string;
  paymentIcon: string;
}

export interface IPaymentHistory extends IPaymentDetails {
  date: string;
  number: string;
  paymentIcon: string;
  status: ERecurringPaymentLogStatus;
}

export interface IGiftCard {
  balance: number;
  code: string;
  expiry: string;
  giftcardId: number;
  initialBalance: number;
  name: string;
  variant: string;
}

export interface IGiftCardDetails extends IGiftCard {
  details: string;
  isExpired: boolean;
  message: string;
  redemptions: IRedemption[];
}

export interface IRedemption {
  redemptionDate: string;
  total: number;
}

export enum ERecurringPaymentLogStatus {
  FailedPayment = 0,
  SuccessfulPayment = 1,
  Suspended = 4,
  Deactivated = 5,
  SuspendedFuture = 6,
  PendingDeactivation = 7,
  Finished = 10,
  Warning = 11,
  Processing = 12,
  InvalidState = 99
}

export enum ETicketType {
  membership = 'membership',
  booking = 'booking',
  giftCard = 'giftCard'
}

export enum EPaymentFrequenciesShort {
  'TICKETS.PAYMENT_FREQUENCY.DAY' = 1,
  'TICKETS.PAYMENT_FREQUENCY.WK',
  'TICKETS.PAYMENT_FREQUENCY.MO',
  'TICKETS.PAYMENT_FREQUENCY.YR'
}

export enum EPaymentFrequencies {
  'TICKETS.PAYMENT_FREQUENCY.DAILY' = 1,
  'TICKETS.PAYMENT_FREQUENCY.WEEKLY',
  'TICKETS.PAYMENT_FREQUENCY.MONTHLY',
  'TICKETS.PAYMENT_FREQUENCY.YEARLY'
}

export const CBarcode = {
  width: 360,
  height: 80
};

export const CMembership = 'membership'; //upgrade
export const CMembershipRenewal = 'membership-renewal';
export const CBooking = 'booking';
export type TManageOrderType = typeof CBooking | typeof CMembership | typeof CMembershipRenewal;

export type TMembershipChipIcon = 'check_circle' | 'error' | 'cancel';

export interface IMembershipChipColorIcon {
  icon: TMembershipChipIcon;
  color: TColors;
}
